import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaperPlane,
  faPaperclip,
  faTimes,
  faUserMd,
  faCircle,
  faSpinner,
  faImage,
  faVideo,
} from '@fortawesome/free-solid-svg-icons';
import styles from './ChatWindow.module.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Global kill-switch for zombie Fast Refresh socket reconnect loops
if (window.__globalSocketInstance) {
  try {
    window.__globalSocketInstance.disconnect();
    window.__globalSocketInstance.close();
  } catch (e) {}
}

const ChatWindow = ({ doctor, patient, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [roomId, setRoomId] = useState(null);
  const [connected, setConnected] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);
  const socketRef = useRef(null);

  const token = localStorage.getItem('token');
  const loggedInUserId = localStorage.getItem('userId');

  // Determine if logged-in user is acting as the doctor
  const isDocMode = (!doctor && !!patient) || (!!doctor && (doctor === loggedInUserId || doctor?._id === loggedInUserId || localStorage.getItem('role') === 'doctor'));

  const patientId = isDocMode ? (patient?._id || patient) : loggedInUserId;
  const doctorId = isDocMode ? loggedInUserId : (doctor?._id || doctor);
  const partnerUser = isDocMode ? patient : doctor;

  // ─── Format timestamp to "10:42 AM" ──────────────────────────────────────
  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  // ─── Auto-scroll to latest message ───────────────────────────────────────
  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // ─── Initialize room + socket ─────────────────────────────────────────────
  useEffect(() => {
    if (!patientId || !doctorId || !token) return;

    let room;

    const initChat = async () => {
      try {
        // 1. Get or create room
        const { data } = await axios.post(
          '/api/chat/room',
          { patientId, doctorId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        room = data.room;
        setRoomId(room._id);

        // 2. Fetch message history
        const historyRes = await axios.get(`/api/chat/history/${room._id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setMessages(historyRes.data.messages || []);
      } catch (err) {
        console.error('Chat init error:', err);
      } finally {
        setLoadingHistory(false);
      }

      // 3. Connect socket reliably to backend port 8080
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
      if (window.__globalSocketInstance) {
        try { window.__globalSocketInstance.disconnect(); } catch(e) {}
      }

      const socket = io(BACKEND_URL, {
        auth: { token },
        transports: ['websocket', 'polling'],
        reconnectionAttempts: 5,
      });
      socketRef.current = socket;
      window.__globalSocketInstance = socket;

      socket.on('connect', () => {
        setConnected(true);
        socket.emit('join_room', room._id);
        // Mark existing messages as read
        socket.emit('mark_read', { roomId: room._id, userId: loggedInUserId });
      });

      socket.on('disconnect', () => setConnected(false));

      socket.on('receive_message', (msg) => {
        setMessages((prev) => {
          // Deduplicate by _id
          if (prev.find((m) => m._id === msg._id)) return prev;
          return [...prev, msg];
        });
      });
    };

    initChat();

    return () => {
      if (socketRef.current) {
        socketRef.current.off('receive_message');
        socketRef.current.off('connect');
        socketRef.current.off('disconnect');
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      if (window.__globalSocketInstance) {
        try { window.__globalSocketInstance.disconnect(); } catch(e) {}
        window.__globalSocketInstance = null;
      }
    };
  }, [patientId, doctorId, token, loggedInUserId]);

  // ─── Send text message ────────────────────────────────────────────────────
  const handleSend = () => {
    if (!inputText.trim() || !roomId || !socketRef.current) return;

    socketRef.current.emit('send_message', {
      roomId,
      senderId: loggedInUserId,
      senderRole: isDocMode ? 'doctor' : 'patient',
      content: inputText.trim(),
      mediaUrl: null,
      mediaType: 'none',
    });

    setInputText('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ─── Upload media then emit ───────────────────────────────────────────────
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !roomId || !socketRef.current) return;

    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);

      const { data } = await axios.post('/api/chat/upload', form, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      socketRef.current.emit('send_message', {
        roomId,
        senderId: loggedInUserId,
        senderRole: isDocMode ? 'doctor' : 'patient',
        content: '',
        mediaUrl: data.mediaUrl,
        mediaType: data.mediaType,
      });
    } catch (err) {
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // ─── Render a single message bubble ──────────────────────────────────────
  const renderBubble = (msg) => {
    const isMine = String(msg.senderId) === String(loggedInUserId);

    return (
      <div
        key={msg._id || msg.createdAt}
        className={`${styles.bubbleRow} ${isMine ? styles.bubbleRowRight : styles.bubbleRowLeft}`}
      >
        {!isMine && (
          <div className={styles.avatarSmall}>
            <FontAwesomeIcon icon={faUserMd} />
          </div>
        )}

        <div className={`${styles.bubble} ${isMine ? styles.bubbleMine : styles.bubbleTheirs}`}>
          {/* Text content */}
          {msg.content && <p className={styles.bubbleText}>{msg.content}</p>}

          {/* Image media */}
          {msg.mediaType === 'image' && msg.mediaUrl && (
            <img
              src={`${BACKEND_URL}${msg.mediaUrl}`}
              alt="Shared media"
              className={styles.mediaBubble}
            />
          )}

          {/* Video media */}
          {msg.mediaType === 'video' && msg.mediaUrl && (
            <video controls className={styles.mediaBubble}>
              <source src={`${BACKEND_URL}${msg.mediaUrl}`} />
              Your browser does not support this video.
            </video>
          )}

          <span className={styles.bubbleTime}>{formatTime(msg.createdAt)}</span>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.chatWindowOverlay}>
      <div className={styles.chatPanel}>
        {/* ─── HEADER ──────────────────────────────────────────────────── */}
        <div className={styles.chatHeader}>
          <div className={styles.chatHeaderLeft}>
            <div className={styles.docAvatarLg}>
              <FontAwesomeIcon icon={faUserMd} />
            </div>
            <div className={styles.chatHeaderInfo}>
              <span className={styles.chatHeaderName}>
                {isDocMode ? `${partnerUser?.firstName || partnerUser?.name || 'Patient'} ${partnerUser?.lastName || ''}` : `Dr. ${partnerUser?.firstName || 'Doctor'} ${partnerUser?.lastName || ''}`}
              </span>
              <span className={styles.chatHeaderSpec}>
                <FontAwesomeIcon
                  icon={faCircle}
                  style={{
                    fontSize: '0.45rem',
                    color: connected ? '#34d399' : '#94a3b8',
                    marginRight: '5px',
                  }}
                />
                {connected ? 'Online' : 'Connecting...'} • {isDocMode ? 'Patient' : (partnerUser?.specialty || 'Specialist')}
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className={styles.closeBtn}
            aria-label="Close chat"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* ─── MESSAGE LIST ─────────────────────────────────────────────── */}
        <div className={styles.messageList}>
          {loadingHistory ? (
            <div className={styles.loadingState}>
              <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '1.5rem', color: '#2d6a4f' }} />
              <span>Loading conversation history...</span>
            </div>
          ) : messages.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyStateIcon}>
                <FontAwesomeIcon icon={faUserMd} />
              </div>
              <p className={styles.emptyStateTitle}>
                Start a conversation with {isDocMode ? (partnerUser?.firstName || partnerUser?.name || 'your patient') : `Dr. ${partnerUser?.firstName || 'Doctor'}`}
              </p>
              <p className={styles.emptyStateSub}>
                Messages are encrypted and visible only to you and your doctor.
              </p>
            </div>
          ) : (
            messages.map(renderBubble)
          )}
          <div ref={bottomRef} />
        </div>

        {/* ─── INPUT BAR ────────────────────────────────────────────────── */}
        <div className={styles.inputBar}>
          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*,video/*"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />

          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className={styles.attachBtn}
            aria-label="Attach media"
            disabled={uploading}
            title="Attach image or video"
          >
            {uploading ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <FontAwesomeIcon icon={faPaperclip} />
            )}
          </button>

          <textarea
            className={styles.msgInput}
            placeholder="Type a message..."
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
          />

          <button
            type="button"
            onClick={handleSend}
            disabled={!inputText.trim() || !connected}
            className={styles.sendBtn}
            aria-label="Send message"
          >
            <FontAwesomeIcon icon={faPaperPlane} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatWindow;
